/* iTechVista — third-party dependency guard
   ---------------------------------------------------------------------------
   279 of the 387 tool pages load code from somebody else's server, and 13 of
   those requests already fail. Not one page says so. The 3D model stager still
   renders when its lighting map 404s — unlit, in silence. A visitor doesn't
   conclude "a CDN moved a file"; they conclude the tool isn't very good.

   This watches for third-party resources that fail and does two things:
     * tells the visitor honestly that the page is degraded and it isn't them
     * leaves a structured record so a nightly check finds out before a
       customer does

   WHAT IT WILL AND WON'T CLAIM
   The expensive mistake here would be a banner that cries wolf. An ad blocker
   killing an analytics beacon is not a broken tool, and a page that shows a
   warning every visit teaches people to ignore warnings. So:

     * Shopify's own hosts, this store, and a list of known analytics / ads /
       app hosts never raise the banner. They are recorded, nothing more.
     * HTTP error statuses on fetch/XHR raise it only when the URL points at
       a FILE. Tools probe endpoints on purpose and handle the miss; nobody
       404s a .js or a .hdr on purpose.
     * A failed <script src> or stylesheet or font is never intentional, so
       those always raise it.
     * An element carrying its own onerror is a page with a fallback plan,
       not a broken page, so it is recorded and kept quiet.

   SIX DETECTION PATHS, because the known failures arrive by six routes
     1. window 'error' in the capture phase — <script>, <link>, <img> that are
        in the document. Covers papaparse and the Google Fonts stylesheets.
     2. the Image constructor and the createElement pair — images that never
        enter the document, whose error events reach nothing at all because a
        detached node's propagation path is just itself.
     3. document.fonts 'loadingerror' — a webfont declared in CSS fails
        silently everywhere else. Covers the Billabong.ttf on jsdelivr.
     4. fetch and XMLHttpRequest. Covers the 3D stager: three.js pulls its
        lighting map through FileLoader, which is fetch, so it never touches
        paths 1 or 2. The first version of this file missed that page -- the
        one page that prompted the whole idea -- and check-dep-guard.mjs is
        what caught it.
     5. PerformanceObserver on resource timing — a backstop for anything the
        first four miss, but only where responseStatus is genuinely readable.
     6. a size comparison across each host's own resources, which is the only
        thing that catches a cross-origin @import being blocked. See the
        comment on checkOpaqueFailures for why nothing simpler works.

   If this file never loads, every page behaves exactly as it does today. It
   only ever adds. Every entry point is wrapped, because a bug in a warning
   system must not be able to break the thing it is warning about. */
(function () {
  'use strict';

  var SETTLE_MS = 1200;   // collect failures for this long before saying anything
  var MAX_LOG = 50;

  var failures = [];
  var shown = false;
  var timer = null;

  /* The stylesheet is a URL, not a <link>. Styling a banner that appears on
     about 13 of 387 pages is not worth a render-blocking request on the other
     374, so it is fetched only if there turns out to be something to show.
     Read here because document.currentScript is only valid while this file is
     executing, and this file is loaded synchronously. */
  var cssUrl = (document.currentScript && document.currentScript.dataset)
    ? document.currentScript.dataset.css
    : '';

  window.__itvDeps = failures;   // read by rebuild/tools/check-externals.mjs

  /* Hosts that are infrastructure rather than tool dependencies. A failure
     here is somebody's ad blocker, or an app being an app, and is not
     something to interrupt a visitor about. */
  var IGNORED = [
    'cdn.shopify.com', 'cdn.shopifycloud.com', 'shopifycdn.net',
    'monorail-edge.shopifysvc.com', 'shopifysvc.com', 'myshopify.com',
    'itechvista.com',
    'google-analytics.com', 'googletagmanager.com', 'analytics.google.com',
    'doubleclick.net', 'googlesyndication.com', 'googleadservices.com',
    'facebook.com', 'facebook.net', 'connect.facebook.net',
    'clarity.ms', 'hotjar.com', 'hotjar.io', 'segment.com', 'segment.io',
    'judge.me', 'judgeme.com', 'klaviyo.com', 'tiktok.com', 'bing.com',
    'pinterest.com', 'snapchat.com', 'criteo.com', 'taboola.com',

    /* Payments and wallets. These are checkout infrastructure, not a tool's
       dependency, and they are exactly the hosts a privacy extension or a
       corporate proxy is most likely to block. Found the hard way: the
       banner fired on the CART page saying a file from www.paypal.com could
       not be reached -- on the one page in the store where an unexplained
       warning costs money. */
    'paypal.com', 'paypalobjects.com', 'venmo.com',
    'stripe.com', 'stripe.network',
    'shop.app', 'shopify.com',
    'klarna.com', 'klarnaservices.com', 'afterpay.com', 'clearpay.co.uk',
    'affirm.com', 'payments-amazon.com', 'amazonpay.com',
    'cdn-apple.com', 'pay.google.com'

    /* Deliberately NOT here: googleapis.com, gstatic.com, apple.com,
       amazon.com, google.com. The first draft listed those and the test
       matrix failed two rows within a minute -- fonts.googleapis.com is a
       genuine tool dependency on 247 pages, and silencing the whole domain
       hid the six pages that ask Google Fonts for "Segoe UI", which is not
       a Google font and never arrives. Payment hosts only, named as
       narrowly as they can be. */
  ];

  /* The banner only ever speaks on a TOOL page.
   
     Its whole argument is "this tool is degraded and it isn't your fault",
     which is meaningless on a cart, a product page or a collection -- and
     actively harmful mid-checkout. Failures are still RECORDED everywhere,
     because the nightly check wants them; they just do not raise a banner
     anywhere but /pages/. */
  function onToolPage() {
    return /^\/pages\//.test(String(location.pathname));
  }

  /* A 404 from fetch or XHR is ambiguous in a way a failed <script src> never
     is: plenty of tools probe an endpoint on purpose and handle the miss
     themselves. But a 404 on a FILE — a script, a font, a texture, a data
     table — is not a probe, it is a dependency that has moved.

     This is the line between the two, and it is the line the 3D model stager
     falls on: three.js pulls its lighting map through FileLoader, which is
     fetch, so the request looks like an API call and lands as a 404 on
     `royal_esplanade_1k.hdr`. Without this rule that page stays silent, which
     is the exact failure this whole file was written for. */
  var ASSET = /\.(js|mjs|css|json|wasm|ttf|otf|woff2?|eot|png|jpe?g|gif|svg|webp|avif|bmp|ico|mp3|wav|ogg|mp4|webm|glb|gltf|hdr|exr|obj|mtl|fbx|bin|csv|txt|xml|zip|data)(\?|#|$)/i;

  function isAsset(url) {
    try {
      return ASSET.test(new URL(String(url), location.href).pathname);
    } catch (e) {
      return false;
    }
  }

  function hostOf(url) {
    try {
      return new URL(String(url), location.href).hostname.toLowerCase();
    } catch (e) {
      return '';
    }
  }

  function isIgnorable(url) {
    var s = String(url || '');
    // data:, blob:, about: and inline sources are not somebody else's server
    if (!s || /^(data|blob|about|javascript):/i.test(s)) return true;

    var host = hostOf(s);
    if (!host || host === location.hostname) return true;

    for (var i = 0; i < IGNORED.length; i++) {
      var d = IGNORED[i];
      if (host === d || host.slice(-(d.length + 1)) === '.' + d) return true;
    }
    return false;
  }

  /* record() is the only way anything enters the list. `visible` decides
     whether this failure is allowed to raise the banner; everything is
     recorded either way, because the nightly check wants the quiet ones too. */
  function record(url, kind, visible) {
    try {
      var s = String(url || '');

      for (var i = 0; i < failures.length; i++) {
        if (failures[i].url === s) return;    // one entry per URL
      }

      var ignorable = isIgnorable(s);

      /* The cap bounds memory, but it must never be what decides whether a
         visitor is told. A page with fifty quiet entries -- and the opaque
         check can produce a lot of those on a heavily-cached page -- would
         otherwise fill the list and swallow the one failure worth showing. */
      if (failures.length >= MAX_LOG && (ignorable || !visible)) return;
      var entry = {
        url: s,
        host: hostOf(s),
        kind: kind,
        thirdParty: !ignorable,
        raisesBanner: !!visible && !ignorable && onToolPage()
      };
      failures.push(entry);

      // Structured and greppable, so a headless run can read it back out.
      if (window.console && console.warn) {
        console.warn('[itv-dep] ' + kind + ' failed: ' + s);
      }

      if (entry.raisesBanner) schedule();
    } catch (e) { /* a logger must never throw */ }
  }

  function schedule() {
    if (shown || timer) return;
    timer = setTimeout(function () {
      timer = null;
      withStyles(render);
    }, SETTLE_MS);
  }

  /* ---------------------------------------------------------------- banner */

  /* Load the stylesheet, then build. If it 404s or is blocked we build anyway
     — an unstyled paragraph saying the tool is degraded is still worth more
     than silence, which is the whole argument this file exists to make. */
  function withStyles(next) {
    if (!cssUrl) return next();
    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = cssUrl;
    link.addEventListener('load', next);
    link.addEventListener('error', next);
    document.head.appendChild(link);
  }

  function render() {
    try {
      if (shown) return;

      var hosts = [];
      for (var i = 0; i < failures.length; i++) {
        var f = failures[i];
        if (f.raisesBanner && hosts.indexOf(f.host) === -1) hosts.push(f.host);
      }
      if (!hosts.length) return;

      /* Put it at the top of the page's own content, not the top of the
         document -- it is a statement about this tool, and above the store
         header it would read as a site-wide outage.

         The fallback chain matters more than it looks. The first version
         only knew the two standard containers, and /pages/windows-iv10
         renders through a bespoke template that has neither -- so the
         failure was detected, the banner was built, and it was dropped on
         the floor. #MainContent is Dawn's main landmark and exists on every
         template; body is the last resort and always works. */
      /* One selector at a time, in order of preference. A single
         querySelector with a comma-separated list would NOT do this: it
         returns the first match in document order, so #MainContent -- which
         wraps everything -- wins over the stage inside it every time, and
         the banner ends up above the store's breadcrumb instead of inside
         the tool it is talking about. */
      var stage = null;
      var where = ['.itv-tool__stage', '.itv-page__body', '#MainContent', 'main'];
      for (var w = 0; w < where.length && !stage; w++) {
        stage = document.querySelector(where[w]);
      }
      if (!stage) stage = document.body;
      if (!stage) return;

      var wrap = document.createElement('div');
      wrap.className = 'itvdep-note';
      /* role="status" and not "alert": this is worth knowing, not worth
         interrupting whatever a screen reader is currently saying. */
      wrap.setAttribute('role', 'status');

      var text = document.createElement('p');
      text.className = 'itvdep-note__text';
      text.appendChild(document.createTextNode(
        'Some of this tool didn’t load. ' +
        (hosts.length === 1 ? 'A file' : 'Files') + ' hosted by ' +
        hosts.slice(0, 2).join(' and ') +
        (hosts.length > 2 ? ' and ' + (hosts.length - 2) + ' other' +
          (hosts.length > 3 ? 's' : '') : '') +
        ' couldn’t be reached, so parts of the page may be missing or ' +
        'look wrong. It isn’t something you did.'
      ));

      var close = document.createElement('button');
      close.type = 'button';
      close.className = 'itvdep-note__close';
      close.setAttribute('aria-label', 'Dismiss this notice');
      close.appendChild(document.createTextNode('Dismiss'));
      close.addEventListener('click', function () {
        if (wrap.parentNode) wrap.parentNode.removeChild(wrap);
      });

      wrap.appendChild(text);
      wrap.appendChild(close);
      stage.insertBefore(wrap, stage.firstChild);

      /* Set only now, and deliberately not earlier. `shown` suppresses every
         later attempt, so setting it before the insert means one missing
         container silences the page permanently -- which is exactly how the
         bespoke-template case went unnoticed. */
      shown = true;
    } catch (e) { /* never let the warning break the page */ }
  }

  /* ------------------------------------------------------------ detection */

  /* An element carrying its own onerror is being handled by whoever wrote it
     — the classic `<img onerror="this.src=fallback">`. That is a page with a
     plan, not a page that is broken, so it is recorded and kept quiet.

     Note this reads the onerror PROPERTY only, which is the useful line to
     draw. three.js registers its handler with addEventListener and is
     invisible to this check, which is the right answer: it does have a
     handler, but the tool pages pass it no onError callback, so the failure
     still ends in silence. That is exactly the case worth a banner. */
  function isHandled(el) {
    try { return typeof el.onerror === 'function'; } catch (e) { return false; }
  }

  function imageFailed(el) {
    record(el.currentSrc || el.src, 'image', !isHandled(el));
  }

  // 1. resource elements in the document
  window.addEventListener('error', function (ev) {
    try {
      var el = ev && ev.target;
      if (!el || el === window || !el.tagName) return;   // script errors, not loads
      var tag = el.tagName.toLowerCase();
      var src = el.src || el.href || '';
      if (!src) return;

      if (tag === 'script') record(src, 'script', !isHandled(el));
      else if (tag === 'link' && /stylesheet/i.test(el.rel || '')) record(src, 'stylesheet', true);
      else if (tag === 'img' || tag === 'image') imageFailed(el);
      else if (tag === 'audio' || tag === 'video' || tag === 'source') record(src, tag, !isHandled(el));
    } catch (e) { /* ignore */ }
  }, true);

  /* 2. images that never enter the document. Their error events reach nothing
        — a detached node's propagation path is just itself — so the only way
        to hear them is at the point of creation. Both routes are covered:
        `new Image()`, and the createElement pair, which is what three.js and
        most canvas code actually use. */
  function watchIfImage(el, tag) {
    try {
      if (el && (tag === 'img' || tag === 'IMG')) {
        el.addEventListener('error', function () { imageFailed(el); });
      }
    } catch (e) { /* ignore */ }
    return el;
  }

  try {
    var NativeImage = window.Image;
    if (typeof NativeImage === 'function') {
      var Wrapped = function (w, h) {
        var img = w === undefined
          ? new NativeImage()
          : (h === undefined ? new NativeImage(w) : new NativeImage(w, h));
        return watchIfImage(img, 'img');
      };
      Wrapped.prototype = NativeImage.prototype;
      window.Image = Wrapped;
    }

    var createEl = document.createElement;
    document.createElement = function (tag) {
      return watchIfImage(createEl.apply(this, arguments), tag);
    };

    var createNS = document.createElementNS;
    document.createElementNS = function (ns, tag) {
      return watchIfImage(createNS.apply(this, arguments), tag);
    };
  } catch (e) { /* ignore */ }

  // 3. webfonts declared in CSS
  try {
    if (document.fonts && document.fonts.addEventListener) {
      document.fonts.addEventListener('loadingerror', function (ev) {
        try {
          var faces = (ev && ev.fontfaces) || [];
          for (var i = 0; i < faces.length; i++) {
            // the src is the only place the URL survives on a FontFace
            var src = String(faces[i].src || '');
            var m = src.match(/url\(["']?([^"')]+)/);
            record(m ? m[1] : (faces[i].family || 'webfont'), 'font', true);
          }
          if (!faces.length) record('webfont', 'font', false);
        } catch (e2) { /* ignore */ }
      });
    }
  } catch (e) { /* ignore */ }

  // 4. fetch / XHR. Network rejections raise the banner; HTTP statuses don't.
  try {
    var nativeFetch = window.fetch;
    if (typeof nativeFetch === 'function') {
      window.fetch = function (input, init) {
        var url = (input && input.url) ? input.url : input;
        return nativeFetch.apply(this, arguments).then(function (res) {
          if (res && !res.ok) record(url, 'fetch ' + res.status, isAsset(url));
          return res;
        }, function (err) {
          record(url, 'fetch', true);
          throw err;
        });
      };
    }
  } catch (e) { /* ignore */ }

  try {
    var open = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      try {
        this.addEventListener('error', function () { record(url, 'xhr', true); });
        this.addEventListener('load', function () {
          if (this.status >= 400) record(url, 'xhr ' + this.status, isAsset(url));
        });
      } catch (e2) { /* ignore */ }
      return open.apply(this, arguments);
    };
  } catch (e) { /* ignore */ }

  /* 6. stylesheets pulled in with @import, which are invisible to every path
        above: there is no element, so no error event; it is not fetch or XHR;
        and the CSSOM gives nothing away -- a blocked import and a working one
        both report `rule.styleSheet` present and both throw SecurityError on
        cssRules. Measured on /pages/windows-iv10, which is the one page in
        387 that the first version of this file missed.

        Resource timing does distinguish them, but not on its own: a blocked
        response reads decodedBodySize 0, and so does a perfectly good
        response from a host that does not send Timing-Allow-Origin. Those
        two are identical from here.

        So this calibrates against the host itself. If the SAME host also
        served something with a real body, that host is demonstrably
        reporting sizes to us -- and a sibling reading zero is genuinely
        empty rather than merely opaque. If the host never reports a size,
        nothing can be concluded and nothing is said.

        On windows-iv10 that resolves cleanly: fonts.googleapis.com serves
        Cascadia Code at 4,222 bytes and Segoe UI -- which is not a Google
        font and never could be -- at zero. */
  function checkOpaqueFailures() {
    try {
      var entries = performance.getEntriesByType('resource');
      var reporting = {};       // hosts known to tell us real sizes
      var suspects = [];

      for (var i = 0; i < entries.length; i++) {
        var e = entries[i];
        var host = hostOf(e.name);
        if (!host) continue;
        if (e.decodedBodySize > 0) { reporting[host] = true; continue; }
        if (e.transferSize > 0) { reporting[host] = true; continue; }
        suspects.push(e);
      }

      for (var j = 0; j < suspects.length; j++) {
        var s2 = suspects[j];
        if (!reporting[hostOf(s2.name)]) continue;      // host tells us nothing
        var kind = s2.initiatorType || 'resource';
        if (kind !== 'css' && kind !== 'link' && kind !== 'script' &&
            kind !== 'img' && kind !== 'image') continue;
        record(s2.name, kind + ' empty', true);
      }
    } catch (e) { /* ignore */ }
  }

  /* Runs on a timer rather than from the observer above, because a page where
     this is the only failure has nothing else to trigger it.

     Twice, and the second time is not belt-and-braces. Hanging it off `load`
     alone was measured at SIXTEEN SECONDS to a banner on
     /pages/windows-iv10, because `load` waits for every font and image on
     the page and that one is heavy. By then a visitor has formed their
     opinion and possibly left. The DOMContentLoaded pass catches it while
     the page is still settling; the load pass is the safety net for anything
     that failed later. record() de-duplicates by URL, so running twice
     cannot produce two banners. */
  function scheduleOpaqueCheck() {
    try {
      setTimeout(checkOpaqueFailures, 1500);
      if (document.readyState === 'complete') setTimeout(checkOpaqueFailures, 400);
      else window.addEventListener('load', function () { setTimeout(checkOpaqueFailures, 400); });
    } catch (e) { /* ignore */ }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleOpaqueCheck);
  } else {
    scheduleOpaqueCheck();
  }

  // 5. backstop. responseStatus reads 0 on cross-origin responses without a
  //    Timing-Allow-Origin header, so this only ever adds what it can prove.
  try {
    if (window.PerformanceObserver) {
      new PerformanceObserver(function (list) {
        var entries = list.getEntries();
        for (var i = 0; i < entries.length; i++) {
          var e = entries[i];
          if (e.responseStatus && e.responseStatus >= 400) {
            var kind = e.initiatorType || 'resource';
            var loud = isAsset(e.name) || kind === 'script' || kind === 'link' ||
                       kind === 'css' || kind === 'img' || kind === 'image';
            record(e.name, kind + ' ' + e.responseStatus, loud);
          }
        }
      }).observe({ type: 'resource', buffered: true });
    }
  } catch (e) { /* ignore */ }
})();
